import React, { useEffect, useState } from 'react';
import {
  Activity,
  ArrowRight,
  Camera,
  Check,
  ChevronRight,
  Clock3,
  Dumbbell,
  HeartPulse,
  Menu,
  MessageCircle,
  Play,
  Salad,
  ShoppingBasket,
  Sparkles,
  Target,
  Video,
  X,
  Zap,
} from 'lucide-react';

const navItems = [
  { href: '#funktionen', label: 'Funktionen' },
  { href: '#so-gehts', label: 'So geht’s' },
  { href: '#ernaehrung', label: 'Ernährung' },
  { href: '#faq', label: 'FAQ' },
];

const features = [
  {
    icon: Sparkles,
    number: '01',
    title: 'Dein KI-Coach',
    text: 'Frag jederzeit nach einem Workout, einer Rezeptidee oder dem nächsten sinnvollen Schritt. Dein Coach denkt mit.',
  },
  {
    icon: Dumbbell,
    number: '02',
    title: 'Training nach Maß',
    text: 'Einheiten, die sich an dein Ziel, dein Niveau, deine Zeit und das vorhandene Equipment anpassen.',
  },
  {
    icon: Camera,
    number: '03',
    title: 'Essen einfach erfassen',
    text: 'Fotografieren, beschreiben oder Barcode scannen – FitMitAi hilft dir, Kalorien und Makros im Blick zu behalten.',
  },
  {
    icon: ShoppingBasket,
    number: '04',
    title: 'Smarter einkaufen',
    text: 'Aus Rezepten werden automatisch Einkaufslisten, damit gute Entscheidungen auch im Alltag leicht bleiben.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Ziel festlegen',
    text: 'Sag uns, was du erreichen möchtest und wie dein Alltag aussieht.',
  },
  {
    number: '02',
    title: 'Plan erhalten',
    text: 'Du bekommst einen persönlichen Mix aus Training, Ernährung und Routinen.',
  },
  {
    number: '03',
    title: 'Dranbleiben',
    text: 'Tracke deinen Fortschritt und lass deinen Plan laufend mit dir wachsen.',
  },
];

const faqs = [
  {
    question: 'Was ist FitMitAi?',
    answer:
      'FitMitAi ist eine KI-gestützte Fitness- und Ernährungsplattform. Sie verbindet persönliche Trainingspläne, Rezepte, Ernährungs-Tracking und motivierende Unterstützung in einer Anwendung.',
  },
  {
    question: 'Brauche ich ein Fitnessstudio?',
    answer:
      'Nein. Deine Einheiten werden an das Equipment angepasst, das dir zur Verfügung steht – auch Training ganz ohne Geräte ist möglich.',
  },
  {
    question: 'Ersetzt FitMitAi medizinische Beratung?',
    answer:
      'Nein. Die Inhalte dienen der allgemeinen Information und Unterstützung. Bei Beschwerden oder gesundheitlichen Fragen solltest du ärztlichen Rat einholen.',
  },
  {
    question: 'Wie funktioniert das Abonnement?',
    answer:
      'Der Dienst kostet 4,99 € inklusive Mehrwertsteuer pro Woche und verlängert sich wöchentlich. Die Abrechnung erfolgt über deine Mobilfunkrechnung. Du kannst dein Abonnement jederzeit kündigen.',
  },
];

function Brand({ inverse = false }) {
  return (
    <a className={`brand ${inverse ? 'brand--inverse' : ''}`} href="/" aria-label="FitMitAi Startseite">
      <span className="brand__mark" aria-hidden="true">
        <Activity size={22} strokeWidth={2.6} />
      </span>
      <span className="brand__word">FitMitAi</span>
    </a>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.documentElement.lang = 'de';
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="site-shell">
      <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
        <div className="nav-wrap">
          <Brand />
          <nav className="desktop-nav" aria-label="Hauptnavigation">
            {navItems.map((item) => (
              <a href={item.href} key={item.href}>
                {item.label}
              </a>
            ))}
          </nav>
          <div className="header-actions">
            <a className="button button--small button--dark desktop-cta" href="/s/">
              Jetzt starten <ArrowRight size={16} />
            </a>
            <button
              className="menu-toggle"
              type="button"
              aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="mobile-nav" aria-label="Mobile Navigation">
            {navItems.map((item) => (
              <a href={item.href} key={item.href} onClick={() => setMenuOpen(false)}>
                {item.label} <ChevronRight size={18} />
              </a>
            ))}
            <a className="button button--primary" href="/s/">
              Jetzt starten <ArrowRight size={17} />
            </a>
          </nav>
        )}
      </header>

      <main>
        <section className="hero">
          <div className="hero__glow hero__glow--one" />
          <div className="hero__glow hero__glow--two" />
          <div className="section-wrap hero__grid">
            <div className="hero__copy">
              <span className="eyebrow">
                <Sparkles size={15} /> Fitness, die zu dir passt
              </span>
              <h1>
                Dein Alltag.
                <span>Dein Plan.</span>
                Dein Fortschritt.
              </h1>
              <p className="hero__lead">
                Training, Ernährung und echte Motivation in einer smarten App. FitMitAi macht aus
                deinen Zielen einen Plan, der sich an dein Leben anpasst.
              </p>
              <div className="hero__actions">
                <a className="button button--primary" href="/s/">
                  Meinen Plan starten <ArrowRight size={19} />
                </a>
                <a className="text-link" href="#funktionen">
                  <span className="play-icon"><Play size={14} fill="currentColor" /></span>
                  FitMitAi entdecken
                </a>
              </div>
              <div className="hero__trust" aria-label="Vorteile">
                <span><Check size={15} /> Individuell</span>
                <span><Check size={15} /> Jederzeit verfügbar</span>
                <span><Check size={15} /> Auf Deutsch</span>
              </div>
            </div>

            <div className="hero__visual">
              <div className="hero__image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1546483875-ad9014c88eba?auto=format&fit=crop&w=1200&q=88"
                  alt="Sportlerin beim Training im Freien"
                />
                <div className="hero__image-shade" />
                <div className="hero__image-label">
                  <span>Heute</span>
                  <strong>Du bist stärker als gestern.</strong>
                </div>
              </div>

              <div className="float-card float-card--workout">
                <div className="float-card__icon"><Zap size={18} fill="currentColor" /></div>
                <div>
                  <span>Nächstes Workout</span>
                  <strong>Full Body · 28 Min.</strong>
                </div>
              </div>

              <div className="float-card float-card--progress">
                <div className="progress-ring"><span>78%</span></div>
                <div>
                  <span>Wochenziel</span>
                  <strong>Fast geschafft!</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="hero__metrics">
            <div className="section-wrap metric-grid">
              <div><strong>24/7</strong><span>KI-Coach an deiner Seite</span></div>
              <div><strong>1 App</strong><span>für Training &amp; Ernährung</span></div>
              <div><strong>100%</strong><span>auf dein Ziel abgestimmt</span></div>
              <div><strong>7</strong><span>verfügbare Sprachen</span></div>
            </div>
          </div>
        </section>

        <section className="feature-section section-space" id="funktionen">
          <div className="section-wrap">
            <div className="section-heading section-heading--split">
              <div>
                <span className="eyebrow eyebrow--light">Alles in einer App</span>
                <h2>Ein Coach, der<br />mit dir mitdenkt.</h2>
              </div>
              <p>
                Kein starrer Standardplan. FitMitAi verbindet deine Ziele, Vorlieben und Fortschritte
                zu einer persönlichen Begleitung, die sich jeden Tag neu auf dich einstellt.
              </p>
            </div>

            <div className="feature-grid">
              {features.map(({ icon: Icon, number, title, text }) => (
                <article className="feature-card" key={number}>
                  <div className="feature-card__top">
                    <span className="feature-card__icon"><Icon size={24} /></span>
                    <span className="feature-card__number">{number}</span>
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <span className="feature-card__line" />
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="personal-section section-space">
          <div className="section-wrap personal-grid">
            <div className="personal-visual">
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1200&q=86"
                alt="Persönliche Betreuung beim Krafttraining"
              />
              <div className="coach-bubble">
                <MessageCircle size={19} />
                <p><strong>Dein Coach:</strong> Heute passt ein kurzes Krafttraining perfekt in deinen Plan.</p>
              </div>
            </div>
            <div className="personal-copy">
              <span className="eyebrow eyebrow--light">Persönlich statt pauschal</span>
              <h2>Dein Plan darf sich verändern. Genau wie du.</h2>
              <p className="section-lead">
                Wenig Zeit? Neue Ziele? Kein Equipment? FitMitAi passt dein Programm an, ohne dass
                du wieder bei null anfangen musst.
              </p>
              <ul className="check-list">
                <li><span><Check size={17} /></span> Workouts für dein aktuelles Fitnesslevel</li>
                <li><span><Check size={17} /></span> Übungen für Zuhause, draußen oder im Studio</li>
                <li><span><Check size={17} /></span> Klare Videoanleitungen für jede Bewegung</li>
                <li><span><Check size={17} /></span> Fortschritt, Ziele und Routinen an einem Ort</li>
              </ul>
              <a className="inline-link" href="/s/">
                Persönlichen Plan erstellen <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        <section className="dashboard-section section-space">
          <div className="section-wrap dashboard-grid">
            <div className="dashboard-copy">
              <span className="eyebrow">Klar. Motivierend. Übersichtlich.</span>
              <h2>Heute zählt.<br />Nicht irgendwann.</h2>
              <p>
                Dein persönliches Dashboard zeigt dir genau das, was jetzt wichtig ist – ohne
                Informationsflut und ohne schlechtes Gewissen.
              </p>
              <div className="mini-benefits">
                <div><Target size={21} /><span><strong>Tagesziele</strong>Machbare Schritte statt großer Vorsätze</span></div>
                <div><HeartPulse size={21} /><span><strong>Fortschritt</strong>Erkenne, was wirklich funktioniert</span></div>
                <div><Clock3 size={21} /><span><strong>Flexibel</strong>Dein Plan passt sich deiner Zeit an</span></div>
              </div>
            </div>

            <div className="phone-stage" aria-label="Beispiel des FitMitAi Dashboards">
              <div className="orbit orbit--one" />
              <div className="orbit orbit--two" />
              <div className="phone">
                <div className="phone__speaker" />
                <div className="phone__screen">
                  <div className="phone__header">
                    <span>Guten Morgen, Mia</span>
                    <div className="phone__avatar">M</div>
                  </div>
                  <p>Dein Fokus heute</p>
                  <article className="phone__hero-card">
                    <span>TRAINING</span>
                    <strong>Core &amp; Mobility</strong>
                    <small>24 Min. · Ohne Geräte</small>
                    <button type="button" aria-label="Workout abspielen"><Play size={14} fill="currentColor" /></button>
                  </article>
                  <div className="phone__stats">
                    <article><Activity size={17} /><strong>6.420</strong><small>Schritte</small></article>
                    <article><Zap size={17} /><strong>1.680</strong><small>kcal</small></article>
                  </div>
                  <article className="phone__meal">
                    <div className="meal-art"><Salad size={23} /></div>
                    <div><small>DEIN MITTAGESSEN</small><strong>Green Power Bowl</strong></div>
                    <ChevronRight size={18} />
                  </article>
                  <nav className="phone__nav" aria-label="App Vorschau">
                    <Activity size={17} /><Dumbbell size={17} /><Salad size={17} /><MessageCircle size={17} />
                  </nav>
                </div>
              </div>
              <div className="stage-note stage-note--top"><Sparkles size={16} /> KI-optimiert</div>
              <div className="stage-note stage-note--bottom"><Check size={16} /> Tagesziel erreicht</div>
            </div>
          </div>
        </section>

        <section className="steps-section section-space" id="so-gehts">
          <div className="section-wrap">
            <div className="section-heading section-heading--center">
              <span className="eyebrow eyebrow--light">Einfach loslegen</span>
              <h2>Drei Schritte. Ein Plan, der bleibt.</h2>
              <p>Du musst nicht alles auf einmal ändern. Du brauchst nur den richtigen nächsten Schritt.</p>
            </div>
            <div className="steps-grid">
              {steps.map((step, index) => (
                <article className="step-card" key={step.number}>
                  <span className="step-card__number">{step.number}</span>
                  <div className="step-card__dot" />
                  {index < steps.length - 1 && <div className="step-card__connector" />}
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="nutrition-section section-space" id="ernaehrung">
          <div className="section-wrap nutrition-grid">
            <div className="nutrition-copy">
              <span className="eyebrow">Ernährung ohne Rätselraten</span>
              <h2>Gutes Essen passt auch in einen vollen Tag.</h2>
              <p>
                Entdecke Rezepte, die zu deinem Ziel und deinen Vorlieben passen. Erfasse Mahlzeiten
                schnell und behalte Kalorien sowie Makros entspannt im Blick.
              </p>
              <div className="nutrition-pills">
                <span><Camera size={16} /> Foto-Tracking</span>
                <span><Salad size={16} /> Persönliche Rezepte</span>
                <span><ShoppingBasket size={16} /> Einkaufslisten</span>
              </div>
              <a className="button button--cream" href="/s/">
                Ernährung neu denken <ArrowRight size={18} />
              </a>
            </div>
            <div className="nutrition-visual">
              <img
                src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1200&q=88"
                alt="Bunte, ausgewogene Mahlzeit"
              />
              <div className="macro-card">
                <div className="macro-card__head">
                  <span>Heutige Makros</span>
                  <strong>68%</strong>
                </div>
                <div className="macro-row"><span>Protein</span><i><b style={{ width: '76%' }} /></i><em>92 g</em></div>
                <div className="macro-row"><span>Kohlenhydrate</span><i><b style={{ width: '61%' }} /></i><em>146 g</em></div>
                <div className="macro-row"><span>Fette</span><i><b style={{ width: '49%' }} /></i><em>42 g</em></div>
              </div>
            </div>
          </div>
        </section>

        <section className="video-strip">
          <div className="video-strip__image">
            <img
              src="https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1800&q=88"
              alt="Gemeinsames funktionelles Training"
            />
          </div>
          <div className="video-strip__content">
            <span><Video size={18} /> Video-Coaching</span>
            <h2>Sieh, wie es geht.<br />Spür, dass es wirkt.</h2>
            <p>Klare Anleitungen geben dir Sicherheit bei jeder Wiederholung.</p>
          </div>
        </section>

        <section className="faq-section section-space" id="faq">
          <div className="section-wrap faq-grid">
            <div className="faq-intro">
              <span className="eyebrow eyebrow--light">Noch Fragen?</span>
              <h2>Gut zu wissen.</h2>
              <p>Hier findest du die wichtigsten Antworten rund um FitMitAi.</p>
              <a className="inline-link" href="/hilfe.html">
                Alle Fragen ansehen <ArrowRight size={18} />
              </a>
            </div>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <details key={faq.question} open={index === 0}>
                  <summary>
                    <span>{faq.question}</span>
                    <span className="faq-plus" aria-hidden="true">+</span>
                  </summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta__shape final-cta__shape--one" />
          <div className="final-cta__shape final-cta__shape--two" />
          <div className="section-wrap final-cta__content">
            <span className="eyebrow">Dein nächster Schritt</span>
            <h2>Bereit, dich wieder richtig gut zu fühlen?</h2>
            <p>Starte mit einem Plan, der nicht irgendeinen Alltag meint – sondern deinen.</p>
            <a className="button button--primary button--large" href="/s/">
              Jetzt mit FitMitAi starten <ArrowRight size={20} />
            </a>
            <small>4,99 € inkl. MwSt. pro Woche · Abrechnung über die Mobilfunkrechnung · jederzeit kündbar</small>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="section-wrap">
          <div className="footer-main">
            <div className="footer-brand">
              <Brand inverse />
              <p>Dein smarter Begleiter für Training, Ernährung und gesunde Routinen.</p>
            </div>
            <div className="footer-column">
              <strong>Entdecken</strong>
              <a href="#funktionen">Funktionen</a>
              <a href="#so-gehts">So geht’s</a>
              <a href="#ernaehrung">Ernährung</a>
              <a href="/s/">Jetzt starten</a>
            </div>
            <div className="footer-column">
              <strong>Rechtliches</strong>
              <a href="/impressum.html">Impressum</a>
              <a href="/agb.html">AGB</a>
              <a href="/widerruf.html">Widerruf</a>
              <a href="/datenschutz.html">Datenschutz</a>
              <a href="/hilfe.html">Hilfe &amp; FAQ</a>
            </div>
            <div className="footer-column footer-contact">
              <strong>Kontakt</strong>
              <a href="mailto:help@niffomobile.com">help@niffomobile.com</a>
              <p>Niffo Mobile B.V.<br />Stationsplein 28<br />1382 AD Weesp, Niederlande</p>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2026 FitMitAi. Alle Rechte vorbehalten.</span>
            <span>Fitness neu gedacht.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
